import { useEffect, useId, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { formatTitle, sectionTitle } from '../utils/pageTitle';

// Tiny shared store for the page-specific part of the tab title (e.g. the open talent's name).
// Each caller owns one entry; the latest non-empty entry wins, so a nested caller (a drawer
// opened over a task) can't wipe the page's title, and unmounting restores the previous one.
const entries = new Map();
let detail = '';
const listeners = new Set();
const publish = () => {
  const next = [...entries.values()].filter(Boolean).pop() || '';
  if (next === detail) return;
  detail = next;
  listeners.forEach(listener => listener());
};
const subscribe = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getDetail = () => detail;

// Detail pages call this with the loaded entity name ('' while loading); removed on unmount.
export const usePageTitle = value => {
  const owner = useId();
  const next = typeof value === 'string' ? value.trim() : '';
  useEffect(() => {
    entries.set(owner, next);
    publish();
  }, [owner, next]);
  useEffect(() => () => {
    entries.delete(owner);
    publish();
  }, [owner]);
};

// Mount once inside the Router: keeps document.title in step with the route and the page detail.
export const DocumentTitle = () => {
  const { pathname } = useLocation();
  const current = useSyncExternalStore(subscribe, getDetail);
  const title = formatTitle(current, sectionTitle(pathname));
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
};

export default usePageTitle;
